/**
 * Gate approvals inside the pinned "Needs your attention" card.
 *
 * Fed by GET /admin/governance/gates-attention, which classifies every
 * SUBMITTED gate with the engine's own eligibility check:
 *   - Needs your decision: the signed-in admin may decide it now.
 *   - No eligible approver: nobody can decide it. "Assign an approver" is
 *     offered ONLY where the admin is a member of that workspace; elsewhere it
 *     is a single oversight line that opens the governance queue (view-only).
 *   - Awaiting others: oversight only, never actionable.
 *   - Could not be evaluated: its own line — never folded into "none".
 * A read that failed says Unavailable; it never renders as an empty list.
 */
import { Link } from "react-router-dom";

import { gatesAttentionIsEmpty } from "../gatesAttention";

import { ADMIN_HOME_GOVERNANCE_EXCEPTIONS_HREF } from "./AdminHomePrimaryActions";

import { typography } from "@/design";
import type {
  GatesAttention,
  GatesAttentionItem,
} from "@/features/administration/api/administration.api";
import { cn } from "@/lib/utils";

const TOP_ITEMS = 5;
const itemClass =
  "block rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 hover:bg-slate-50";

function gateProjectHref(projectId: string): string {
  return `/projects/${projectId}/governance`;
}

function gateLabel(g: GatesAttentionItem): string {
  const where = [g.projectName?.trim(), g.workspaceName?.trim()]
    .filter(Boolean)
    .join(" · ");
  const place = where || "Project";
  return g.stepName ? `${place} · ${g.stepName}` : place;
}

export type AdminHomeGatesSectionProps = {
  gates: GatesAttention | null;
  unavailable: boolean;
};

export function AdminHomeGatesSection({
  gates,
  unavailable,
}: AdminHomeGatesSectionProps) {
  if (unavailable || gates === null) {
    return (
      <p
        className={typography.muted}
        data-testid="admin-home-gates-unavailable"
      >
        Gate approvals: Unavailable
      </p>
    );
  }
  if (gatesAttentionIsEmpty(gates)) return null;

  const mine = gates.needsYourDecision;
  const assignable = gates.noEligibleApprover.filter((g) => g.canAssign);
  const oversightNoApprover = gates.noEligibleApprover.filter(
    (g) => !g.canAssign,
  );

  return (
    <div className="space-y-4" data-testid="admin-home-gates">
      {mine.length > 0 ? (
        <div data-testid="admin-home-gates-mine">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h3 className={cn(typography.label, "text-slate-800")}>
              Needs your decision
            </h3>
            <span
              className="text-sm font-semibold tabular-nums text-slate-900"
              data-testid="admin-home-gates-mine-count"
            >
              {mine.length}
            </span>
          </div>
          <ul className="space-y-1.5">
            {mine.slice(0, TOP_ITEMS).map((g) => (
              <li key={g.submissionId}>
                <Link
                  to={gateProjectHref(g.projectId)}
                  className={itemClass}
                  data-testid={`admin-home-gate-${g.submissionId}`}
                >
                  {gateLabel(g)}
                </Link>
              </li>
            ))}
          </ul>
          {mine.length > TOP_ITEMS ? (
            <p className={cn(typography.muted, "mt-1")}>
              +{mine.length - TOP_ITEMS} more
            </p>
          ) : null}
        </div>
      ) : null}

      {assignable.length > 0 ? (
        <div data-testid="admin-home-gates-assign">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h3 className={cn(typography.label, "text-slate-800")}>
              No eligible approver — assign one
            </h3>
            <span
              className="text-sm font-semibold tabular-nums text-slate-900"
              data-testid="admin-home-gates-assign-count"
            >
              {assignable.length}
            </span>
          </div>
          <ul className="space-y-1.5">
            {assignable.slice(0, TOP_ITEMS).map((g) => (
              <li key={g.submissionId}>
                <Link
                  to={gateProjectHref(g.projectId)}
                  className={itemClass}
                  data-testid={`admin-home-gate-assign-${g.submissionId}`}
                >
                  {gateLabel(g)} · Assign an approver
                </Link>
              </li>
            ))}
          </ul>
          {assignable.length > TOP_ITEMS ? (
            <p className={cn(typography.muted, "mt-1")}>
              +{assignable.length - TOP_ITEMS} more
            </p>
          ) : null}
        </div>
      ) : null}

      {oversightNoApprover.length > 0 ? (
        <p className={typography.muted} data-testid="admin-home-gates-oversight-none">
          Oversight: {oversightNoApprover.length}{" "}
          {oversightNoApprover.length === 1 ? "gate has" : "gates have"} no
          eligible approver, in workspaces you are not a member of.{" "}
          <Link
            to={ADMIN_HOME_GOVERNANCE_EXCEPTIONS_HREF}
            className="text-blue-700 hover:text-blue-800"
          >
            View governance queue
          </Link>
        </p>
      ) : null}

      {gates.awaitingOthersCount > 0 ? (
        <p className={typography.muted} data-testid="admin-home-gates-others">
          Oversight: {gates.awaitingOthersCount}{" "}
          {gates.awaitingOthersCount === 1 ? "gate is" : "gates are"} awaiting
          others.
        </p>
      ) : null}

      {gates.notEvaluableCount > 0 ? (
        <p
          className={typography.muted}
          data-testid="admin-home-gates-not-evaluable"
        >
          {gates.notEvaluableCount}{" "}
          {gates.notEvaluableCount === 1 ? "gate" : "gates"} could not be
          evaluated.
        </p>
      ) : null}

      {gates.truncated ? (
        <p className={typography.muted} data-testid="admin-home-gates-truncated">
          Showing the oldest {gates.scanned} of {gates.totalSubmitted} submitted
          gates.
        </p>
      ) : null}
    </div>
  );
}
